/** @jsxImportSource @emotion/react */
import { StatisticsDateRangePicker } from '../components/StatisticsDateRangePicker';
import { useStatisticsDateRange } from '../hooks/useStatisticsDateRange';
import { css } from '@emotion/react';
import React, { useState, useEffect, useCallback } from 'react';
import { message, Row, Col, Card, Typography, Select, Space } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AccessFilterHeader, AccessSubTab, AccessPeriod } from './components/AccessFilterHeader';
import { AccessSubTabs } from './components/AccessSubTabs';
import { AccessOverviewCards } from './components/AccessOverviewCards';
import {
  FeatureDoughnutChartCard,
  TrendLineChartCard,
} from './components/AccessOverviewCharts';
import {
  getAccessOverviewData,
  exportAccessOverviewExcel,
  AccessOverviewResponse,
} from '../services/statistics.service';
import html2canvas from 'html2canvas';
import { StatisticsAccessUserPage } from './pages/StatisticsAccessUserPage';
import { StatisticsAccessFrequencyPage } from './pages/StatisticsAccessFrequencyPage';
import { StatisticsAccessAvgTimePage } from './pages/StatisticsAccessAvgTimePage';

const { Title } = Typography;

interface StatisticsAccessPageProps {
  activeMainTab: 'profile' | 'access';
  onMainTabChange: (val: 'profile' | 'access') => void;
}

// Dropdown options for overview time filter per Figma Image 3
const OVERVIEW_TIME_OPTIONS = [
  { value: 'today', label: 'Hôm nay' },
  { value: 'this_month', label: 'Tháng này' },
  { value: 'this_year', label: 'Năm này' },
  { value: 'last_30_days', label: '30 ngày gần nhất' },
  { value: 'last_12_months', label: '12 tháng gần nhất' },
  { value: 'last_3_years', label: '3 năm gần nhất' },
];

export const StatisticsAccessPage: React.FC<StatisticsAccessPageProps> = ({
  activeMainTab,
  onMainTabChange,
}) => {
  const [subTab, setSubTab] = useState<AccessSubTab>('overview');
  const [selectedPeriod, setSelectedPeriod] = useState<AccessPeriod | undefined>(undefined);
  const { period, dateRange, displayDateRange, status: dateRangeStatus, changePeriod: setPeriod,
    applyDateRange: applyRange, beginResolution, finishResolution, failResolution, invalidate } = useStatisticsDateRange();
  const overviewVersion = React.useRef(0);
  const [childExportReady, setChildExportReady] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [data, setData] = useState<AccessOverviewResponse | null>(null);

  const overviewContentRef = React.useRef<HTMLDivElement>(null);
  const userPageRef = React.useRef<any>(null);
  const freqPageRef = React.useRef<any>(null);
  const avgTimePageRef = React.useRef<any>(null);

  const fetchData = useCallback(async () => {
    if (subTab !== 'overview') return;
    const version = ++overviewVersion.current;
    const token = beginResolution();
    setLoadFailed(false);
    setLoading(true);
    try {
      const res = await getAccessOverviewData({
        period: period as AccessPeriod,
        from_date: dateRange[0] || undefined,
        to_date: dateRange[1] || undefined,
      });
      if (version !== overviewVersion.current) return;
      finishResolution(token, res.resolved_date_range);
      setData(res);
    } catch (err: any) {
      if (version !== overviewVersion.current) return;
      setLoadFailed(true);
      failResolution(token);
      console.error('Fetch access overview error:', err);
      message.error('Không thể tải dữ liệu thống kê lượt truy cập');
    } finally {
      if (version === overviewVersion.current) setLoading(false);
    }
  }, [period, dateRange, subTab, beginResolution, finishResolution, failResolution]);

  useEffect(() => {
    fetchData();
    return () => { overviewVersion.current += 1; invalidate(); };
  }, [fetchData, invalidate]);

  const handleSubTabChange = (val: AccessSubTab) => {
    setChildExportReady(false);
    setSubTab(val);
  };

  const handleTimeSelectChange = (val?: AccessPeriod) => {
    setSelectedPeriod(val);
    setPeriod(val || 'all');
  };
  const applyDateRange = (range: [string | null, string | null]) => {
    setSelectedPeriod(undefined);
    applyRange(range);
  };

  const handleExportExcel = async () => {
    if (subTab === 'overview' ? loading || loadFailed || dateRangeStatus === 'loading' || dateRangeStatus === 'error' : !childExportReady) return;
    setExportLoading(true);
    try {
      if (subTab === 'overview') {
        if (!overviewContentRef.current) return;
        const canvas = await html2canvas(overviewContentRef.current, {
          scale: 2,
          backgroundColor: '#ffffff',
          useCORS: true,
          logging: false,
        });
        const imgBase64 = canvas.toDataURL('image/png');

        let timeRangeLabel = 'Tất cả';
        if (period === 'today') {
          timeRangeLabel = `${dayjs().format('DD/MM/YYYY')} - ${dayjs().format('DD/MM/YYYY')}`;
        } else if (period === 'this_month') {
          timeRangeLabel = `${dayjs().startOf('month').format('DD/MM/YYYY')} - ${dayjs().endOf('month').format('DD/MM/YYYY')}`;
        } else if (period === 'this_year') {
          timeRangeLabel = `${dayjs().startOf('year').format('DD/MM/YYYY')} - ${dayjs().endOf('year').format('DD/MM/YYYY')}`;
        } else if (period === 'last_30_days') {
          timeRangeLabel = `${dayjs().subtract(30, 'day').format('DD/MM/YYYY')} - ${dayjs().format('DD/MM/YYYY')}`;
        } else if (period === 'last_12_months') {
          timeRangeLabel = `${dayjs().subtract(12, 'month').format('DD/MM/YYYY')} - ${dayjs().format('DD/MM/YYYY')}`;
        } else if (period === 'last_3_years') {
          timeRangeLabel = `${dayjs().subtract(3, 'year').format('DD/MM/YYYY')} - ${dayjs().format('DD/MM/YYYY')}`;
        } else if (period === 'custom' && (displayDateRange[0] || displayDateRange[1])) {
          timeRangeLabel = `${displayDateRange[0] ? dayjs(displayDateRange[0]).format('DD/MM/YYYY') : '...'} - ${displayDateRange[1] ? dayjs(displayDateRange[1]).format('DD/MM/YYYY') : '...'}`;
        }

        const blob = await exportAccessOverviewExcel(imgBase64, timeRangeLabel);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tong_quan_luot_truy_cap_${Date.now()}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (subTab === 'users' && userPageRef.current) {
        await userPageRef.current.handleExport();
      } else if (subTab === 'frequency' && freqPageRef.current) {
        await freqPageRef.current.handleExport();
      } else if (subTab === 'avg_time' && avgTimePageRef.current) {
        await avgTimePageRef.current.handleExport();
      }
    } catch (err: any) {
      console.error('Export overview excel error:', err);
      message.error('Xuất báo cáo Excel thất bại');
    } finally {
      setExportLoading(false);
    }
  };

  const subTabsNode = (
    <AccessSubTabs subTab={subTab} onSubTabChange={handleSubTabChange} />
  );

  return (
    <div css={pageContainerStyle}>
      <AccessFilterHeader
        activeMainTab={activeMainTab}
        onMainTabChange={onMainTabChange}
        onExportExcel={handleExportExcel}
        exportLoading={exportLoading}
        exportDisabled={subTab === 'overview' ? loading || loadFailed || dateRangeStatus === 'loading' || dateRangeStatus === 'error' : !childExportReady}
      />

      {subTab === 'overview' && (
        <Card css={overviewMainCardStyle}>
          {subTabsNode}

          {/* Title & Time Filter Bar per Figma Image 2 */}
          <div css={overviewFilterRowStyle}>
            <Title level={4} style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#000000' }}>
              Tổng quan Lượt truy cập
            </Title>
            <Space size={12}>
              <Select
                placeholder="Chọn thời gian"
                allowClear
                value={selectedPeriod}
                onChange={handleTimeSelectChange}
                style={{ width: 180 }}
                className={selectedPeriod ? 'bold-select-value' : ''}
                suffixIcon={<DownOutlined style={{ color: '#595959' }} />}
                options={OVERVIEW_TIME_OPTIONS}
              />
              <StatisticsDateRangePicker value={displayDateRange} onChange={applyDateRange}
                status={dateRangeStatus} onRetry={fetchData} showApply />
            </Space>
          </div>

          {/* Screenshot container for Excel Export */}
          <div ref={overviewContentRef} style={{ background: '#ffffff', borderRadius: 8 }}>
            {/* Top Row per Wireframe 2: Left 2x2 Cards (Col 12) + Right Doughnut Chart (Col 12) */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} lg={12}>
                <AccessOverviewCards loading={loading} period={period as AccessPeriod} cards={data?.cards} />
              </Col>
              <Col xs={24} lg={12}>
                <FeatureDoughnutChartCard
                  loading={loading}
                  totalAccess={data?.cards?.totalAccess}
                  featureChartData={data?.featureChartData}
                />
              </Col>
            </Row>

            {/* Bottom Row per Wireframe 2: Full Width Line Chart (Col 24) */}
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <TrendLineChartCard
                  loading={loading}
                  trendChartData={data?.trendChartData}
                />
              </Col>
            </Row>
          </div>
        </Card>
      )}

      {subTab === 'users' && (
        <StatisticsAccessUserPage ref={userPageRef} subTabs={subTabsNode} onExportAvailabilityChange={setChildExportReady} />
      )}

      {subTab === 'frequency' && (
        <StatisticsAccessFrequencyPage ref={freqPageRef} subTabs={subTabsNode} onExportAvailabilityChange={setChildExportReady} />
      )}

      {subTab === 'avg_time' && (
        <StatisticsAccessAvgTimePage ref={avgTimePageRef} subTabs={subTabsNode} onExportAvailabilityChange={setChildExportReady} />
      )}
    </div>
  );
};

export default StatisticsAccessPage;

const pageContainerStyle = css`
  background: var(--gray-soft-color);
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const overviewMainCardStyle = css`
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  background: #ffffff;

  .ant-card-body {
    padding: 20px 24px 24px 24px;
  }
`;

const overviewFilterRowStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;

  .bold-select-value .ant-select-selection-item {
    font-weight: 600 !important;
    color: #000000 !important;
  }
`;
